/**
 * Lease Terms Panel
 * P20250925-114 — Key lease terms and conditions
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Shield, CreditCard, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

interface LeaseTermsProps {
  className?: string;
}

export default function LeaseTerms({ className = '' }: LeaseTermsProps) {
  return (
    <Card className={className}>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="w-5 h-5 text-primary" />
          Lease Terms
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Key Terms */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm text-foreground mb-1">
                Secure via a trusted payment provider
              </p>
              <p className="text-sm text-muted-foreground">
                All lease payments processed through a trusted payment provider for buyer and
                seller protection.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm text-foreground mb-1">Monthly Payments</p>
              <p className="text-sm text-muted-foreground">
                Automatic monthly billing. 3% processing fee included per payment. No hidden
                charges.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm text-foreground mb-1">Domain Transfer</p>
              <p className="text-sm text-muted-foreground">
                Domain transfers to you after final payment. DNS control granted immediately upon
                lease start.
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border/50 pt-4 space-y-3">
          <h4 className="font-medium text-sm text-foreground">Key Points</h4>

          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold mt-0.5">•</span>
              <span>
                <strong className="text-foreground">Buy Option:</strong> Purchase at BIN price
                anytime during lease to own immediately
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold mt-0.5">•</span>
              <span>
                <strong className="text-foreground">Early Cancellation:</strong> 30-day notice
                required. Contact support for details
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold mt-0.5">•</span>
              <span>
                <strong className="text-foreground">Missed Payments:</strong> 15-day grace period.
                Domain access suspended after 3 missed payments
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold mt-0.5">•</span>
              <span>
                <strong className="text-foreground">Full Ownership:</strong> Domain ownership
                transfers after all payments completed
              </span>
            </li>
          </ul>
        </div>

        {/* Support Links */}
        <div className="bg-muted/30 p-3 rounded-lg border border-border/50">
          <p className="text-sm text-muted-foreground mb-2">Questions about leasing?</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" size="sm" asChild className="flex-1">
              <Link to="/support">Contact Support</Link>
            </Button>
            <Button variant="outline" size="sm" asChild className="flex-1">
              <Link to="/legal/lease">Full Lease Agreement</Link>
            </Button>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center pt-2">
          By proceeding with a lease, you agree to our lease terms and conditions. This is not a
          rent-to-own agreement.
        </p>
      </CardContent>
    </Card>
  );
}
